import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Layout from '../components/layout';
import { features, opportunities, toolsItems, infrastructure } from '../constants/main';
import Image from 'next/image';
import ContactUsModal from '../components/contactUsModal';

const servicesInImage = [
  [
    "",
    "Networking",
    "Outrun",
    "Artifactory",
    "",
  ],
  [
    "",
    "Storage",
    "Code",
    "",
  ],
  [
    "",
    "Compliance",
    "Disk",
    "Compute",
    "",
  ],
  [
    "",
    "Database",
    "MQ",
    "",
    "",
  ]
];

export default function Home() {
  const router = useRouter();
  const { t } = useTranslation('main');
  const awardButtonRef = useRef();
  const hashPart = router.asPath.split('#');

  const [toolFlag, setToolFlag] = useState(0);

  function mouseMoveEvent(e) {
    const { x, y } = awardButtonRef.current.getBoundingClientRect();
    awardButtonRef.current.style.setProperty('--x', e.clientX - x);
    awardButtonRef.current.style.setProperty('--y', e.clientY - y);
  };

  useEffect(() => {
    if (awardButtonRef.current) {
      awardButtonRef.current.addEventListener('mousemove', mouseMoveEvent);

      return () => awardButtonRef.current?.removeEventListener('mousemove', mouseMoveEvent)
    }
  }, [awardButtonRef])

  const changeTool = (index) => (e) => {
    setToolFlag(index)
  };

  return (
    <Layout title={t('mainTitle')} lang={router.locale} route={router.route} isHeader isFooter>
      <main>
        <section className='main'>
          <div className='main__img'>
            <Image src='/main.webp' alt='Main background' layout='fill' objectFit='cover' unoptimized priority />
          </div>
          <div className='main__content container'>
            <h1 className={router.locale}>{t('title')}</h1>
            <p>{t('description')}</p>
            <div className={`buttons flex ${router.locale}`}>
              <a className='btn cloud' href='/configuration'>{t('createCloud')}</a>
              <ContactUsModal lang={router.locale} dafaultOpen={hashPart[1] && hashPart[1] === 'contact'} />
            </div>
            <a href='https://ibagroupit.com/news/iba-group-wins-in-best-in-cloud-2023/'>
              <button className={`award-winning ${router.locale}`} ref={awardButtonRef}>{t('bestCloud')}</button>
            </a>
          </div>
        </section>

        <div className='main_bg'>
          <section className='ai_security'>
            <div className='ai_text_content'>
              <h2>{t('ai_security_title')}</h2>
              <p className='ai_subtitle'><b>{t("ai_security_subtitle")}</b></p>
              <p>{t("ai_security_description")}</p>
              <ul>
                {
                  Array.from({ length: 4 }).map((_, index) => <li key={index}>{t(`ai_security_p${index + 1}`)}</li>)
                }
              </ul>
            </div>
            <div className='ai_image_col'>
              <div className='ai_image_wrapper'>
                <Image src={"/ai.webp"} alt={t("ai_security_title")} loading='lazy' width={257} height={387} layout='responsive' />
              </div>
            </div>
          </section>

          <section className='features container flex'>
            <div className='main-part'>
              <h2>{t('bestFeatures')}</h2>
              <p>{t('ourMission')}</p>
              <Link href='/about'>
                <a className='btn cloud'>{t('aboutUs')}</a>
              </Link>
            </div>
            <div className='features__description'>
              {features.map((feature, key) => <div key={key} className='features__item'>
                <Image src={feature.img} alt="" />
                <h3>{t(feature.title)}</h3>
                <p className='normal'>{t(feature.description)}</p>
              </div>)}
            </div>
          </section>

          <section className='tools'>
            <h2>{t('tools')}</h2>
            <div className='tools__content'>
              <div className='image_container'>
                <Image
                  src={toolsItems[toolFlag].img}
                  alt='Tool for your business'
                  layout='responsive'
                  width={600}
                  height={398}
                  unoptimized
                />
              </div>
              <div className='tools__content__drop' >
                {toolsItems.map((tool, index) => (
                  <div key={index}>
                    <button onClick={changeTool(index)} type='button' className='tool_trigger'>
                      <span>{toolFlag === index ? <>&#9660;</> : <>&#9658;</>} {t(tool.title)}</span>
                    </button>
                    {index === toolFlag && <p>{t(tool.description)}</p>}
                  </div>))}
              </div>
            </div>
            <a className='btn cloud max_content' href='/location-admin'>{t('learnMore')}
              <span className='sr-only'>{t('learn_more_about_location_admin')}</span>
            </a>
          </section>
        </div>

        <section className='services container'>
          <div className='services__content grid'>
            <h2>{t('growServices')}</h2>
            <p>{t('deployPlatform')}</p>
            <a className='btn cloud max_content' href='/specification'>{t('learnMore')}
              <span className='sr-only'>{t('learn_more_about_specification')}</span>
            </a>
          </div>
          <div className='services_img'>
            {
              servicesInImage.map((servicesInRow, key) => (
                <div key={key} className='services_row'>
                  {
                    servicesInRow.map((serviceName, index) => (
                      <div key={index} className={`services_item ${serviceName.toLocaleLowerCase()}`}>{serviceName}</div>
                    ))
                  }
                </div>
              ))
            }
          </div>
        </section>

        <section className='interface'>
          <div className='opportunities'>
            {opportunities.map((opportunity, key) => <div key={key} className='item'>
              <h3>{t(opportunity.title)}</h3>
              <p className='small-font'>{t(opportunity.description)}</p>
            </div>)}
          </div>
        </section>

        <section className='howitworks'>
          <h2>{t('howItWorks')}</h2>
          <div className='steps'>
            {new Array(4).fill('').map((_, key) => (
              <div key={key} className='step'>
                <div className='circle'><div><div>{key + 1}</div></div></div>
                <p>{t(`step${key + 1}`)}</p>
              </div>)
            )}
          </div>
          <Link href='/configuration'><a><button className='btn cloud'>{t('configure')}</button></a></Link>
        </section>

        <section className='infrastructure container'>
          {infrastructure.map((description, key) => <div key={key}>
            <h3>{t(description.title)}</h3>
            <ul type='square'>
              {description.points.map((point, key) => <li key={key}>{t(point)}
                {point === 'backups' && <Link href='/veeam'><a> {t("veeamLink")}</a></Link>}
              </li>)}
            </ul>
          </div>)}
        </section>

        <hr />

        <section className='terraform'>
          <div className='terraform__content'>
            <h2>{t('terraformTitle')}</h2>
            <h3>{t('subTitleTerraform')}</h3>
            <p>{t('terraformDescription')}</p>
          </div>
          <div className='terraform__img'>
            <Image src='/terraform.webp' alt={t('terraformLogoImg')} loading='lazy' width={590} height={204} layout='responsive' />
          </div>
        </section>

        <section className='confidentialComputing'>
          <div className='confidentialComputing__wrapper'>
            <div className='confidentialComputing__img'>
              <Image src='/confidentialComputing.webp' alt={t('confidentialComputingTitle')} loading='lazy' width={975} height={580} layout='responsive' unoptimized />
            </div>
            <div className='confidentialComputing__content'>
              <h2>{t('confidentialComputingTitle')}</h2>
              <h3>{t('confidentialComputingSubTitle')}</h3>
              <p>{t('confidentialComputingDescription')}</p>
              <a className='btn cloud max_content' href='/confidential-computing'>{t('learnMore')}
                <span className='sr-only'>{t('learn_more_about_confidential_computing')}</span>
              </a>
            </div>
          </div>
        </section>

      </main>
    </Layout>
  )
};

export const getStaticProps = async ({ locale }) => ({
  props: {
    ...await serverSideTranslations(locale, ['common', 'main']),
  },
});
